import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { getThumbnailUrl, getOriginalImageUrl } from "../../utils/imageUtils";
import { SearchInput } from "../../components/SearchInput";
import { Select } from "../../components/ui/Select";
import { Spinner } from "components/ui";

interface Product {
 _id: string;
 title: string;
 handle: string;
 description?: string;
 status: "active" | "draft" | "archived";
 basePrice?: number;
 variants: Array<{
  _id: string;
  name: string;
  price: number;
  compareAtPrice?: number;
  sku: string;
 }>;
 images: Array<{
  url: string;
  isPrimary?: boolean;
 }>;
 createdAt: string;
}

export const PlatformStoreProducts: React.FC = () => {
 const { t } = useTranslation();
 const { storeId } = useParams<{ storeId: string }>();
 const { isAuthenticated } = usePlatformAuth();
 const navigate = useNavigate();
 const [store, setStore] = useState<any>(null);
 const [products, setProducts] = useState<Product[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [debouncedSearch, setDebouncedSearch] = useState("");
 const [statusFilter, setStatusFilter] = useState<string>("");
 const [page, setPage] = useState(1);
 const [total, setTotal] = useState(0);
 const [limit] = useState(20);
 const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
 const [bulkActionLoading, setBulkActionLoading] = useState(false);

 const fetchStore = async () => {
  try {
   const response = await platformApiService.getStore(storeId!);
   setStore(response.data.store);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to fetch store");
  }
 };

 const fetchProducts = useCallback(async () => {
  try {
   setLoading(true);
   setError(null);
   const params: any = {
    page: page.toString(),
    limit: limit.toString(),
    // Backend will automatically return USD for admin users (no conversion)
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(statusFilter && { status: statusFilter }),
   };
   const response = await platformApiService.getStoreProducts(storeId!, params);
   setProducts(response.data.products || []);
   setTotal(response.data.total || 0);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to fetch products");
  } finally {
   setLoading(false);
  }
 }, [storeId, page, limit, debouncedSearch, statusFilter]);

 // Handler for when search is ready from SearchInput component
 // Use useRef to ensure callback never changes
 const handleSearchReadyRef = useRef((searchQuery: string) => {
  setDebouncedSearch(searchQuery);
  setPage(1);
 });

 // Update the ref function to use current state setters
 useEffect(() => {
  handleSearchReadyRef.current = (searchQuery: string) => {
   setDebouncedSearch(searchQuery);
   setPage(1);
  };
 }, []);

 const handleSearchReady = useCallback((searchQuery: string) => {
  handleSearchReadyRef.current(searchQuery);
 }, []);

 useEffect(() => {
  if (!isAuthenticated) {
   navigate("/platform/login", { replace: true });
   return;
  }

  if (storeId) {
   fetchStore();
  }
 }, [isAuthenticated, navigate, storeId]);

 useEffect(() => {
  if (storeId) {
   fetchProducts();
  }
 }, [storeId, fetchProducts]);

 const handleDelete = async (productId: string) => {
  if (!window.confirm("Are you sure you want to delete this product? This will archive it.")) {
   return;
  }

  try {
   await platformApiService.deleteStoreProduct(storeId!, productId);
   setProducts(products.filter((p) => p._id !== productId));
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to delete product");
  }
 };

 const handleStatusChange = async (productId: string, newStatus: string) => {
  try {
   await platformApiService.updateStoreProduct(storeId!, productId, { status: newStatus });
   setProducts(
    products.map((p) =>
     p._id === productId ? { ...p, status: newStatus as any } : p
    )
   );
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to update product status");
  }
 };

 const handleSelectAll = (checked: boolean) => {
  if (checked) {
   setSelectedIds(new Set(products.map((p) => p._id)));
  } else {
   setSelectedIds(new Set());
  }
 };

 const handleSelectOne = (id: string, checked: boolean) => {
  const newSelected = new Set(selectedIds);
  if (checked) {
   newSelected.add(id);
  } else {
   newSelected.delete(id);
  }
  setSelectedIds(newSelected);
 };

 const handleBatchDelete = async () => {
  if (selectedIds.size === 0) return;
  if (!window.confirm(
   `Are you sure you want to PERMANENTLY delete ${selectedIds.size} product(s)?\n\n` +
   `This will:\n` +
   `- Completely remove products from the database\n` +
   `- Delete all associated images from the server\n\n` +
   `This action CANNOT be undone!`
  )) {
   return;
  }

  try {
   setBulkActionLoading(true);
   setError(null);
   const response = await platformApiService.batchDeleteStoreProducts(storeId!, Array.from(selectedIds));
   setSelectedIds(new Set());
   fetchProducts();
   alert(`Successfully deleted ${response.data.deletedCount} product(s) and ${response.data.deletedImages} image(s)`);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to delete products");
  } finally {
   setBulkActionLoading(false);
  }
 };

 const handleBulkStatusChange = async (status: string) => {
  if (selectedIds.size === 0) return;

  try {
   setBulkActionLoading(true);
   setError(null);
   await platformApiService.bulkUpdateStoreProductStatus(storeId!, Array.from(selectedIds), status);
   setSelectedIds(new Set());
   fetchProducts();
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to update product status");
  } finally {
   setBulkActionLoading(false);
  }
 };

 const totalPages = Math.ceil(total / limit);

 if (loading && products.length === 0) {
  return (
   <div className="p-8">
    <div className="flex items-center justify-center">
     <Spinner />
    </div>
   </div>
  );
 }

 return (
  <div className="p-8">
   <div className="max-w-7xl mx-auto">
    {/* Header */}
    <div className="mb-6">
     <Link
      to={`/platform/stores/${storeId}`}
      className="text-indigo-600 hover:text-indigo-800 mb-2 inline-block"
     >
      ← Back to Store
     </Link>
     <h1 className="text-2xl font-bold text-gray-900">
      {store ? `Products - ${store.name}` : "Products"}
     </h1>
     <p className="text-sm text-gray-500 mt-1">
      Manage products for this store. Batch delete will permanently remove products and images.
     </p>
    </div>

    {error && (
     <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700">
      {error}
     </div>
    )}

    {/* Bulk Actions */}
    {selectedIds.size > 0 && (
     <div className="mb-4 p-4 bg-blue-50 border border-blue-200 ">
      <div className="flex items-center justify-between">
       <div className="text-sm font-medium text-blue-900">
        {selectedIds.size} product(s) selected
       </div>
       <div className="flex gap-2">
        <select
         onChange={(e) => handleBulkStatusChange(e.target.value)}
         disabled={bulkActionLoading}
         className="px-3 py-1 text-sm border border-gray-300 "
         defaultValue=""
        >
         <option value="" disabled>Change Status</option>
         <option value="active">Set to Active</option>
         <option value="draft">Set to Draft</option>
         <option value="archived">Set to Archived</option>
        </select>
        <button
         onClick={handleBatchDelete}
         disabled={bulkActionLoading}
         className="px-4 py-1 text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
        >
         {bulkActionLoading ? "Deleting..." : "Batch Delete (Permanent)"}
        </button>
        <button
         onClick={() => setSelectedIds(new Set())}
         disabled={bulkActionLoading}
         className="px-4 py-1 text-sm border border-gray-300 hover:bg-gray-50"
        >
         Clear Selection
        </button>
       </div>
      </div>
     </div>
    )}

    {/* Filters */}
    <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
     <div key="search-input-wrapper">
      <SearchInput
       key="platform-store-products-search-input"
       instanceId="platform-store-products-search-input"
       initialValue=""
       onSearchReady={handleSearchReady}
       debounceMs={500}
       placeholder={t("common.placeholders.searchProducts")}
      />
     </div>
     <Select
      options={[
       { value: "", label: t("admin.orders.allStatuses") },
       { value: "active", label: t("admin.active") },
       { value: "draft", label: t("admin.draft") },
       { value: "archived", label: t("admin.archived") },
      ]}
      value={statusFilter}
      placeholder={t("admin.orders.allStatuses")}
      onChange={(e) => {
       setStatusFilter(e.target.value);
       setPage(1);
      }}
      fullWidth
     />
    </div>

    {/* Products Table */}
    <div className="bg-white shadow overflow-hidden">
     <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
       <thead className="bg-gray-50">
        <tr>
         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
          <input
           type="checkbox"
           checked={selectedIds.size > 0 && selectedIds.size === products.length}
           onChange={(e) => handleSelectAll(e.target.checked)}
           className="rounded border-gray-300"
          />
         </th>
         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-64 max-w-64">
          Product
         </th>
         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">
          Price
         </th>
         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">
          Status
         </th>
         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-28">
          Created
         </th>
         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">
          Actions
         </th>
        </tr>
       </thead>
       <tbody className="bg-white divide-y divide-gray-200">
        {products.length === 0 ? (
         <tr>
          <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
           No products found
          </td>
         </tr>
        ) : (
         products.map((product) => {
          const variant = product.variants?.[0];
          const image = product.images?.find((img) => img.isPrimary) || product.images?.[0];
          return (
           <tr key={product._id}>
            <td className="px-6 py-4 whitespace-nowrap">
             <input
              type="checkbox"
              checked={selectedIds.has(product._id)}
              onChange={(e) => handleSelectOne(product._id, e.target.checked)}
              className="rounded border-gray-300"
             />
            </td>
            <td className="px-6 py-4 w-64 max-w-64">
             <div className="flex items-center gap-3 min-w-0">
              {image && (() => {
               const thumbnailUrl = getThumbnailUrl(image.url);
               const originalUrl = getOriginalImageUrl(image.url);

               return (
                <img
                 src={thumbnailUrl}
                 alt={product.title}
                 className="w-10 h-10 object-cover rounded flex-shrink-0"
                 onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (target.src !== originalUrl) {
                   target.src = originalUrl;
                  }
                 }}
                />
               );
              })()}
              <div className="min-w-0 flex-1">
               <div 
                className="text-sm font-medium text-gray-900 truncate" 
                title={product.title}
               >
                {product.title}
               </div>
               <div className="text-xs text-gray-500">
                {product.variants?.length || 0} variants
               </div>
              </div>
             </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-24">
             ${(product.basePrice || variant?.price || 0).toFixed(2)}
            </td>
            <td className="px-6 py-4 whitespace-nowrap w-32">
             <select
              value={product.status}
              onChange={(e) =>
               handleStatusChange(product._id, e.target.value)
              }
              className="px-2 py-1 text-xs font-semibold border border-gray-300"
             >
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
             </select>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-28">
             {new Date(product.createdAt).toLocaleDateString()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2 w-32">
             <button
              onClick={() => handleDelete(product._id)}
              className="px-3 py-1 text-sm border border-gray-300 hover:bg-gray-50 text-red-600 hover:text-red-700"
             >
              Delete
             </button>
            </td>
           </tr>
          );
         })
        )}
       </tbody>
      </table>
     </div>

     {/* Pagination */}
     {totalPages > 1 && (
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
       <div className="text-sm text-gray-500">
        Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} products
       </div>
       <div className="flex gap-2">
        <button
         onClick={() => setPage(page - 1)}
         disabled={page === 1}
         className="px-3 py-1 text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
        >
         Previous
        </button>
        <button
         onClick={() => setPage(page + 1)}
         disabled={page >= totalPages}
         className="px-3 py-1 text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
        >
         Next
        </button>
       </div>
      </div>
     )}
    </div>
   </div>
  </div>
 );
};

export default PlatformStoreProducts;

